/**
 * Scroll Reveal — lightweight IntersectionObserver-based scroll animations.
 *
 * Usage:
 *   Add `data-scroll-reveal` to any element to fade it in on scroll.
 *   Add `data-scroll-reveal-stagger` to a parent to stagger its direct children.
 *   Add `data-scroll-reveal-delay="N"` (ms) for an explicit delay override.
 *
 * Respects `prefers-reduced-motion`: when the user has requested reduced motion,
 * all elements are made visible immediately with no animation.
 */

const THRESHOLD = 0.12;
const ROOT_MARGIN = '0px 0px -40px 0px';

/**
 * Returns true if the user prefers reduced motion.
 * @returns {boolean}
 */
function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Reveal a single element by removing its hidden class.
 * @param {HTMLElement} el
 */
function revealElement(el) {
  el.classList.add('scroll-reveal--visible');
  el.classList.remove('scroll-reveal--hidden');
}

/**
 * Initialise scroll reveal on all matching elements.
 * Safe to call multiple times (idempotent via dataset flag).
 */
function initScrollReveal() {
  if (prefersReducedMotion()) {
    // Make everything visible immediately — no animation
    document.querySelectorAll('[data-scroll-reveal], [data-scroll-reveal-stagger] > *').forEach((el) => {
      /** @type {HTMLElement} */ (el).style.opacity = '1';
      /** @type {HTMLElement} */ (el).style.transform = 'none';
    });
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const el = /** @type {HTMLElement} */ (entry.target);
        const delay = el.dataset.scrollRevealDelay ? parseInt(el.dataset.scrollRevealDelay, 10) : 0;

        if (delay > 0) {
          setTimeout(() => revealElement(el), delay);
        } else {
          revealElement(el);
        }

        observer.unobserve(el);
      });
    },
    { threshold: THRESHOLD, rootMargin: ROOT_MARGIN }
  );

  // Direct scroll-reveal elements
  document.querySelectorAll('[data-scroll-reveal]:not([data-scroll-reveal-init])').forEach((el) => {
    const htmlEl = /** @type {HTMLElement} */ (el);
    htmlEl.dataset.scrollRevealInit = 'true';
    htmlEl.classList.add('scroll-reveal--hidden');
    observer.observe(htmlEl);
  });

  // Stagger containers — animate each direct child with an incremental delay
  document.querySelectorAll('[data-scroll-reveal-stagger]:not([data-scroll-reveal-init])').forEach((container) => {
    const htmlContainer = /** @type {HTMLElement} */ (container);
    htmlContainer.dataset.scrollRevealInit = 'true';

    const staggerBase = parseInt(htmlContainer.dataset.scrollRevealStagger || '80', 10);

    Array.from(htmlContainer.children).forEach((child, index) => {
      const htmlChild = /** @type {HTMLElement} */ (child);
      htmlChild.classList.add('scroll-reveal--hidden');
      htmlChild.dataset.scrollRevealDelay = String(index * staggerBase);
      observer.observe(htmlChild);
    });
  });
}

// Run on DOMContentLoaded (handles initial page load)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initScrollReveal);
} else {
  initScrollReveal();
}

// Re-run after Shopify section renders (theme editor live updates)
document.addEventListener('shopify:section:load', () => {
  // Reset init flags so newly loaded sections are picked up
  document.querySelectorAll('[data-scroll-reveal-init]').forEach((el) => {
    delete /** @type {HTMLElement} */ (el).dataset.scrollRevealInit;
  });
  initScrollReveal();
});
